import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { customerinvoicesApi } from "../../../api/customerinvoices";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { toast } from "react-hot-toast";
import {
  ArrowLeftIcon,
  DocumentTextIcon,
  BuildingOffice2Icon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { formatter, formattedDate } from "../../../components/Utils";

export const CustomerInvoiceDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (id) {
      fetchInvoice();
    }
  }, [id]);

  const fetchInvoice = async () => {
    try {
      setIsLoading(true);
      const response = await customerinvoicesApi.getCustomerInvoice(id, {
        fields: "id,invoice_number,invoice_date,invoice_due_date,amount,amount_excluding_vat,vat,customer,project",
      });
      if (response.data?.success) {
        setInvoice(response.data.data);
      } else {
        toast.error("Kunne ikke hente faktura");
      }
    } catch (error) {
      console.error("Error fetching customer invoice:", error);
      toast.error("Kunne ikke hente faktura");
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <DashboardHeader title="Kundefaktura" />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Henter faktura...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="flex flex-col h-full">
        <DashboardHeader title="Kundefaktura" />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <DocumentTextIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Faktura ikke funnet</p>
            <button
              onClick={() => navigate("/dashboard/customer-invoices")}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Tilbake til liste
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <DashboardHeader
        title={`Kundefaktura: ${invoice.invoice_number || `#${invoice.id}`}`}
        actions={[
          {
            component: (
              <button
                onClick={() => navigate("/dashboard/customer-invoices")}
                className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                <ArrowLeftIcon className="w-5 h-5" />
                Tilbake
              </button>
            ),
          },
        ]}
      />

      <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8 pb-24">
        <div className="mt-8">
        {/* Invoice Header */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {invoice.invoice_number || `Faktura #${invoice.id}`}
              </h2>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <UserIcon className="w-5 h-5 text-gray-400" />
                  <span className="text-gray-600">
                    <strong>Kunde:</strong> {invoice.customer?.name || "Ukjent"}
                  </span>
                </div>
                {invoice.customer?.customer_number && (
                  <div className="text-gray-600 ml-7">
                    <strong>Kundenummer:</strong> {invoice.customer.customer_number}
                  </div>
                )}
                {invoice.project && (
                  <div className="flex items-center gap-2">
                    <BuildingOffice2Icon className="w-5 h-5 text-gray-400" />
                    <span className="text-gray-600">
                      <strong>Prosjekt:</strong>{" "}
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded">
                        {invoice.project.name}
                      </span>
                    </span>
                  </div>
                )}
                <div className="text-gray-600 ml-7">
                  <strong>Fakturadato:</strong> {formattedDate(invoice.invoice_date)}
                </div>
                {invoice.invoice_due_date && (
                  <div className="text-gray-600 ml-7">
                    <strong>Forfallsdato:</strong> {formattedDate(invoice.invoice_due_date)}
                  </div>
                )}
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Summering</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Beløp ekskl. mva:</span>
                  <span className="font-semibold text-gray-900">
                    {formatter.format(invoice.amount_excluding_vat || 0)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">MVA:</span>
                  <span className="font-semibold text-gray-900">
                    {formatter.format(invoice.vat || 0)}
                  </span>
                </div>
                <div className="flex justify-between pt-2 border-t border-gray-300">
                  <span className="text-lg font-semibold text-gray-900">Totalt:</span>
                  <span className="text-lg font-bold text-gray-900">
                    {formatter.format(invoice.amount || 0)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

